import { useEffect, useState } from "react";
import clsx from "clsx";
import type { PendingDismissal } from "../../hooks/useCockpitData";

interface Props {
  pending: PendingDismissal[];
  onUndo: (alertId: string) => void;
}

/**
 * Gmail-style undo stack, anchored bottom-right of the viewport. One toast
 * per pending dismissal; older ones sit deeper in the stack. Each toast
 * shows a countdown driven by `expiresAt - now`, animated via a 60ms tick
 * (smooth enough for a 5s window, cheap on re-renders).
 *
 * Click UNDO during the window → parent's `onUndo` clears the timer and
 * restores the alert. Window expires → toast disappears as the parent
 * removes it from `pending`; no manual cleanup needed here.
 */
export function UndoToastStack({ pending, onUndo }: Props) {
  if (pending.length === 0) return null;
  return (
    <div className="undo-toast-stack" role="status" aria-live="polite">
      {pending.map((p) => (
        <UndoToast key={p.alert.id} pending={p} onUndo={() => onUndo(p.alert.id)} />
      ))}
    </div>
  );
}

function UndoToast({
  pending,
  onUndo,
}: {
  pending: PendingDismissal;
  onUndo: () => void;
}) {
  const { alert, kind, note, expiresAt } = pending;
  // Force a re-render every 60ms so the countdown number and progress bar
  // stay accurate without each toast running its own rAF loop.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 60);
    return () => window.clearInterval(id);
  }, []);

  const remainingMs = Math.max(0, expiresAt - Date.now());
  const remainingSec = Math.ceil(remainingMs / 1000);
  const progressPct = Math.min(100, (remainingMs / 5000) * 100);

  const dealLabel = alert.dealName ?? null;
  const verb = kind === "solved" ? "SOLVED" : "REMOVED";
  const glyph = kind === "solved" ? "✓" : "✕";

  return (
    <div className={clsx("undo-toast", kind)}>
      <div className="undo-toast-row">
        <span className="undo-toast-glyph" aria-hidden="true">
          {glyph}
        </span>
        <span className="undo-toast-label">
          {verb}
          {dealLabel && <span className="undo-toast-deal"> · {dealLabel}</span>}
        </span>
        <button
          className="undo-toast-undo"
          onClick={onUndo}
          type="button"
        >
          UNDO
        </button>
        <span className="undo-toast-countdown">{remainingSec}s</span>
      </div>
      {note && note.trim() && (
        <div className="undo-toast-note" title={note}>
          Note: <span className="undo-toast-note-text">"{truncate(note, 60)}"</span>
        </div>
      )}
      <div className="undo-toast-progress">
        <div
          className="undo-toast-progress-fill"
          style={{ width: `${progressPct}%` }}
        />
      </div>
    </div>
  );
}

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return s.slice(0, n - 1) + "…";
}
